import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import {
  addExpense,
  attachFiles,
  changeSalary,
  deferLine,
  deleteOwnExpense,
  excludeLine,
  getPayout,
  getRequisites,
  getSalary,
  payeeRef,
  removeAttachment,
  restoreLine,
  stopRecurringLine,
} from '@/store/actions/payout';
import { selectPayout, selectRequisites, selectSalary } from '@/store/selectors/payout';
import { PayoutLine } from '@/store/reducers/payout';

import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import AddExpense from '@/components/payout/AddExpense';
import BottomSheet from '@/components/payout/BottomSheet';
import ExpenseLine from '@/components/payout/ExpenseLine';
import Requisites from '@/components/payout/Requisites';
import SalaryQuickActions from '@/components/payout/SalaryQuickActions';
import { formatMoney, formatMoneyWithUah, formatSigned } from '@/utils/money';

interface Props {
  userId: number;
  name: string;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Everything the salary screen offers, over the top of the grid: the current
 * figure, the quick raises, and this period's expense lines with the admin
 * actions on them.
 *
 * It exists so reviewing a column does not mean leaving the table and losing
 * your place. `onChanged` refetches the grid behind it, because a raise here
 * moves the cell underneath.
 */
const PayeeSheet: React.FC<Props> = ({ userId, name, onClose, onChanged }) => {
  const dispatch = useDispatch();
  const salary = useSelector(selectSalary);
  const payoutData = useSelector(selectPayout);
  const requisites = useSelector(selectRequisites);
  const [{ payout }] = useLanguage();
  const [isAdding, toggleIsAdding] = useToggle(false);
  const [amount, setAmount] = useState('');
  const [showPaid, toggleShowPaid] = useToggle(false);
  const [showHistory, toggleShowHistory] = useToggle(false);

  const target = { userId };

  useEffect(() => {
    dispatch(getSalary(userId));
    dispatch(getPayout({ userId }));
    dispatch(getRequisites({ userId }));
  }, [userId]);

  // The grid holds the figures this sheet edits, so every mutation has to land
  // on both -- and the refetch has to wait for the write. Dispatching a thunk
  // returns its promise; firing onChanged() straight after would read the grid
  // back before the POST had landed.
  const apply = async (run: () => unknown) => {
    await run();
    onChanged();
  };

  const submitSalary = (value: number, note: string) => {
    if (!(value > 0)) return;
    apply(() => dispatch(changeSalary(userId, value, note || undefined)));
    setAmount('');
  };

  const parsed = Number(amount.replace(',', '.'));
  const isReady = salary && salary.userId === userId && payoutData
    && requisites && requisites.payee === payeeRef({ userId });

  return (
    <BottomSheet isReady={Boolean(isReady)} onClose={onClose}>
      <Heading>{name}</Heading>

      {isReady && (
          <>
            <Summary>
              <SummaryLabel>{payout.current_salary}</SummaryLabel>
              <SummaryValue>
                {salary!.current === undefined
                  ? payout.not_set
                  : formatMoney(salary!.current)}
              </SummaryValue>
              <SummaryNote>
                {`${payout.next_payout} · ${moment(payoutData!.period, 'YYYY-MM').format('MMM YYYY')} · `}
                {formatMoneyWithUah(
                  payoutData!.total !== undefined
                    ? payoutData!.total
                    : payoutData!.expensesTotal,
                  payoutData!.expensesTotalUah,
                )}
              </SummaryNote>
            </Summary>

            <SalaryQuickActions current={salary!.current} onPick={submitSalary} />

            <Row>
              <Input
                value={amount}
                inputMode="decimal"
                placeholder={payout.new_amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <Primary
                disabled={!(parsed > 0)}
                onClick={() => submitSalary(parsed, '')}
              >
                {payout.save}
              </Primary>
            </Row>

            <SectionHead>
              <Label>{payout.expenses}</Label>
              <AddLink onClick={() => toggleIsAdding(true)}>
                {payout.add_expense}
              </AddLink>
            </SectionHead>

            {payoutData!.lines.length === 0 && <Empty>{payout.no_expenses}</Empty>}
            {payoutData!.lines.map((line) => (
              <ExpenseLine
                key={line.ref}
                line={line}
                isAdmin
                onDefer={(l: PayoutLine) => apply(() => dispatch(deferLine(l, payoutData!.period, target)))}
                onExclude={(l: PayoutLine) => apply(() => dispatch(excludeLine(l, payoutData!.period, target)))}
                onRestore={(l: PayoutLine) => apply(() => dispatch(restoreLine(l, target)))}
                onStopRecurring={(l: PayoutLine) => apply(() => (
                  dispatch(stopRecurringLine(l, payoutData!.period, target))
                ))}
                onDelete={(l: PayoutLine) => apply(() => (
                  l.expenseId ? dispatch(deleteOwnExpense(l.expenseId, target)) : undefined
                ))}
                onAttach={(l: PayoutLine, files: File[]) => apply(() => (
                  dispatch(attachFiles(l, files, payoutData!.period, target))
                ))}
                onRemoveAttachment={(a) => apply(() => dispatch(removeAttachment(a, target)))}
              />
            ))}

            <Requisites payee={{ userId }} requisite={requisites!.requisite} />

            {Boolean(salary!.history.length) && (
              <>
                <Expander onClick={() => toggleShowHistory(!showHistory)}>
                  {`${payout.salary_history} (${salary!.history.length})`}
                  <Chevron isOpen={showHistory}>›</Chevron>
                </Expander>
                {showHistory && salary!.history.map((record) => (
                  <HistoryRow key={record._id}>
                    <span>{formatMoney(record.amount)}</span>
                    <HistoryRight>
                      <span>{moment(record.effectiveFrom, 'YYYY-MM').format('MMM YYYY')}</span>
                      {record.change !== undefined && record.change !== 0 && (
                        <Change positive={record.change > 0}>
                          {formatSigned(record.change)}
                        </Change>
                      )}
                    </HistoryRight>
                  </HistoryRow>
                ))}
              </>
            )}

            {/* The salary timeline starts at the import, so for most people it
                is a single row. What they actually want to look back at is the
                months that were paid, which is a different list. */}
            {Boolean(salary!.payoutHistory.length) && (
              <>
                <Expander onClick={() => toggleShowPaid(!showPaid)}>
                  {`${payout.paid_history} (${salary!.payoutHistory.length})`}
                  <Chevron isOpen={showPaid}>›</Chevron>
                </Expander>
                {showPaid && salary!.payoutHistory.map((paid) => (
                  <HistoryRow key={paid._id}>
                    <span>{formatMoney(paid.amount)}</span>
                    <HistoryRight>
                      <span>{moment(paid.period, 'YYYY-MM').format('MMM YYYY')}</span>
                    </HistoryRight>
                  </HistoryRow>
                ))}
              </>
            )}
          </>
      )}

      {isAdding && (
        <AddExpense
          onAdd={(body, files) => apply(() => dispatch(addExpense(body, target, files)))}
          onClose={() => toggleIsAdding(false)}
        />
      )}
    </BottomSheet>
  );
};



const Heading = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #21272e;
  margin-bottom: 14px;
`;

const Summary = styled.div`
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 14px;
  text-align: center;
`;

const SummaryLabel = styled.div`
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.2px;
  color: #909599;
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #21272e;
  margin-top: 4px;
`;

const SummaryNote = styled.div`
  font-size: 12px;
  color: #787c80;
  margin-top: 6px;
`;

const Row = styled.div`
  display: flex;
  margin-top: 12px;
`;

const Input = styled.input`
  flex: 1;
  height: 44px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 0 12px;
  font-size: 14px;
  outline: none;

  :focus {
    border-color: #fb7e14;
  }
`;

const Primary = styled.button`
  height: 44px;
  margin-left: 8px;
  padding: 0 18px;
  border: none;
  border-radius: 6px;
  background: #fb7e14;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  :disabled {
    background: #dae1e8;
    cursor: default;
  }
`;

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 22px 0 4px;
`;

const Label = styled.div`
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.2px;
  color: #909599;
  margin: 22px 0 6px;
`;

const AddLink = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #fb7e14;
  cursor: pointer;
`;

const Empty = styled.div`
  font-size: 14px;
  color: #909599;
  padding: 10px 0;
`;

const Expander = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.2px;
  color: #909599;
  margin: 22px 0 6px;
  cursor: pointer;
  user-select: none;
`;

const Chevron = styled.span<{ isOpen: boolean }>`
  font-size: 16px;
  line-height: 1;
  transition: transform 160ms ease;
  transform: rotate(${({ isOpen }) => (isOpen ? '90deg' : '0deg')});
`;

const HistoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #21272e;
  padding: 8px 0;
  border-bottom: solid 1px #f4f6f8;
`;

const HistoryRight = styled.div`
  display: flex;
  align-items: center;
  color: #909599;
  font-size: 12px;
`;

const Change = styled.span<{ positive: boolean }>`
  margin-left: 8px;
  font-weight: 700;
  color: ${({ positive }) => (positive ? '#188038' : '#d9534f')};
`;

export default PayeeSheet;
